/**
 * Private, on-device taste signal for the Recommended feed.
 *
 * Every venue tap is appended to localStorage (id + taxonomy slugs + time);
 * `scoreByHistory` in feedFilter.ts turns that into a soft ordering bias.
 * Nothing leaves the device, there is no account, and clearing site data
 * resets it — deliberately the cheapest honest version of "For you".
 *
 * The history is read once per feed mount (not on every tap), so the grid
 * never reshuffles under the visitor mid-session; taps influence the NEXT
 * visit's ordering.
 */

import type { Restaurant } from "@/types/restaurant";

export interface TapEntry {
  /** Venue id (uuid). */
  id: string;
  cuisine_slugs: string[];
  tag_slugs: string[];
  /** Tap time, epoch ms. */
  at: number;
}

const STORAGE_KEY = "resto_tap_history";
const MAX_ENTRIES = 50;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

function sanitize(raw: unknown, now: number): TapEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: TapEntry[] = [];
  for (const e of raw) {
    if (
      e &&
      typeof e === "object" &&
      typeof (e as TapEntry).id === "string" &&
      typeof (e as TapEntry).at === "number" &&
      now - (e as TapEntry).at < MAX_AGE_MS
    ) {
      const entry = e as TapEntry;
      out.push({
        id: entry.id,
        cuisine_slugs: Array.isArray(entry.cuisine_slugs)
          ? entry.cuisine_slugs.filter((s): s is string => typeof s === "string")
          : [],
        tag_slugs: Array.isArray(entry.tag_slugs)
          ? entry.tag_slugs.filter((s): s is string => typeof s === "string")
          : [],
        at: entry.at,
      });
    }
  }
  return out;
}

/** Newest-first tap history; [] when empty, blocked, or corrupted. */
export function readTapHistory(now = Date.now()): TapEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return sanitize(JSON.parse(raw), now);
  } catch {
    return [];
  }
}

/** Append a venue tap (fire-and-forget; storage failures are swallowed). */
export function recordTap(
  r: Pick<Restaurant, "id" | "cuisine_slugs" | "tag_slugs">,
  now = Date.now(),
): void {
  try {
    const entries = readTapHistory(now);
    entries.unshift({
      id: r.id,
      cuisine_slugs: r.cuisine_slugs ?? [],
      tag_slugs: r.tag_slugs ?? [],
      at: now,
    });
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // Private mode / storage full — the feature simply stays cold.
  }
}
